'use client'

import { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Shield, Eye } from 'lucide-react'

interface AdminModeContextType {
  adminMode: boolean
  setAdminMode: (enabled: boolean) => void
}

const AdminModeContext = createContext<AdminModeContextType>({
  adminMode: true,
  setAdminMode: () => {},
})

export function AdminModeProvider({ children }: { children: ReactNode }) {
  // Start with default value (true) to match server rendering
  const [adminMode, setAdminModeState] = useState(true)

  // Load from localStorage after hydration to avoid mismatch
  useEffect(() => {
    const saved = localStorage.getItem('adminMode')
    if (saved !== null) {
      setAdminModeState(saved === 'true')
    }
  }, [])

  const setAdminMode = (enabled: boolean) => {
    setAdminModeState(enabled)
    localStorage.setItem('adminMode', enabled.toString())
  }

  return (
    <AdminModeContext.Provider value={{ adminMode, setAdminMode }}>
      {children}
    </AdminModeContext.Provider>
  )
}

export function useAdminMode() {
  return useContext(AdminModeContext)
}

interface AdminModeToggleProps {
  isAdmin: boolean
}

export function AdminModeToggle({ isAdmin }: AdminModeToggleProps) {
  const { adminMode, setAdminMode } = useAdminMode()

  if (!isAdmin) {
    return null
  }

  return (
    <Button
      onClick={() => setAdminMode(!adminMode)}
      variant={adminMode ? 'default' : 'outline'}
      size="sm"
      title={adminMode ? 'Admin mode: ON (click to view as regular user)' : 'Admin mode: OFF (click to view as admin)'}
      aria-label={adminMode ? 'Admin mode enabled. Click to switch to user view.' : 'User view enabled. Click to switch to admin mode.'}
      aria-pressed={adminMode}
    >
      {adminMode ? (
        <>
          <Shield className="mr-2 h-4 w-4" aria-hidden="true" />
          Admin Mode
        </>
      ) : (
        <>
          <Eye className="mr-2 h-4 w-4" aria-hidden="true" />
          User View
        </>
      )}
    </Button>
  )
}
